import React, { useState, Fragment } from 'react'
import CalendarDayOfMonth from './CalendarDayOfMonth'
import moment from 'moment'
import CalendarDayOfWeekHeading from './CalendarDayOfWeekHeading'
import CalendarExpandedDay from './CalendarExpandedDay'
import { getLine } from '../utils/calendarUtils'
import { compareDates } from 'utils/time'
import {
  isInstrumentVisibleByTime,
  isInstrumentVisibleOnCalendarDay,
  isInstanceVisibleByTime,
} from '../modules/Instruments'
import {
  expandInstrumentsByInstances,
  getSchedulesList,
  isMultiScheduleMetadata,
  resolveSchedule,
} from '../utils/instrumentUtils'
import {
  hasActiveInstanceVisibleOnCalendarDay,
  hasAbsoluteDeploy,
  hasNowDeploy,
  isAbsoluteDeployScheduleVisibleOnDay,
  isNowDeployScheduleVisibleOnDay,
  isInstanceVisibleOnCalendarDay,
  isPersistentRecurringScheme,
  mapCompletedInstrumentsForDay,
  shouldHideUnconfirmedVisitInstrument,
  shouldHideNonPersistentPendingOnDay,
  shouldKeepInstrumentInCalendarPending,
  shouldShowPersistentOrVisitInstrumentOnDay,
  shouldDisplayPendingInstrumentOnCalendarDay,
} from '../utils/calendarInstrumentUtils'

const CalendarMonthListItem = ({
  month,
  selectMonth,
  selectedMonth,
  index,
  monthIndex,
  selectDay,
  selectedDay,
  formatSubmissionSurvey,
  strings,
  sendEvent,
  selectedLocale,
  fetchInstrument,
  setSubmissionSurvey,
  displayingList,
  humiIsabellaDisabled,
  onLeftArrowClick,
  onRightArrowClick,
  leftArrowDisabled,
  rightArrowDisabled,
  expiredInstruments,
  completedInstruments,
  availableInstruments,
  allInstances,
  studyId,
  selectedLine,
  selectLine,
  participantId,
  firstLogin,
  confirmedVisitKeys = [],
}) => {
  const hasSelectedMonth = !!(selectedMonth?.value && selectedMonth?.year)
  const isActiveMonth =
    hasSelectedMonth &&
    !(
      month.value === selectedMonth.value && month.year === selectedMonth.year
    )
  const isDayViewExpanded = !!selectedDay
  // Only hide non-selected months once selectedMonth is initialized.
  // Otherwise week view can mount with a selected day and empty month,
  // which previously hid every month (and its instruments).
  const showOnlyCurrentMonth =
    hasSelectedMonth && isActiveMonth && isDayViewExpanded

  const [isExpanded, setIsExpanded] = useState(true)

  const isWeekView = selectedLine !== 0
  const daysOfWeek = isWeekView
    ? (month?.days || []).filter((_, idx) => getLine(idx) === selectedLine)
    : month?.days?.slice(0, 7)

  if (showOnlyCurrentMonth) {
    return null
  }

  return (
    <div className='calendar-month-list-item'>
      {selectedLine === 0 ? (
        <div
          className={`calendar-mode-month-panel ${index !== 0 ? 'flex-end' : ''
            }`}>
          {index === 0 ? (
            <div
              className={`calendar-mode-select-month-btn left ${leftArrowDisabled ? 'disabled' : ''
                }`}
              onClick={() => {
                onLeftArrowClick()
              }}>
              <i className='fa fa-chevron-left' />
            </div>
          ) : null}
          <div className='calendar-mode-month-content'>
            <div
              className='calendar-month-item-name'
              onClick={() => {
                selectMonth(month)
                setIsExpanded(!isExpanded)
              }}>
              {month.longName}
            </div>
            {index === 0 ? (
              <div
                className={`calendar-mode-select-month-btn right ${rightArrowDisabled ? 'disabled' : ''
                  }`}
                onClick={() => {
                  onRightArrowClick()
                }}>
                <i className='fa fa-chevron-right' />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {isExpanded ? (
        <>
          <div className='calendar-month-item-day-headings'>
            {daysOfWeek.map((day, idx) => (
              <CalendarDayOfWeekHeading key={idx} date={day} />
            ))}
          </div>
          <div className='calendar-month-item-days'>
            {month?.days?.map((day, idx) => {
              const line = getLine(idx)

              // In week view show only the selected line (week)
              if (isWeekView && line !== selectedLine) {
                return null
              }

              // Cache moment objects and formatted strings to avoid repeated calculations
              const targetDayMoment = moment(day)
              const targetDayString = targetDayMoment
                .clone()
                .locale('en')
                .format('YYYY-MM-DD')
              const isToday = moment().isSame(targetDayMoment, 'd')

              // Helper function to check if all instances are completed
              const areAllInstancesCompleted = (instances) => {
                return instances?.every((instance) => !!instance.completion) ?? false
              }

              // Helper function to check time_of_day constraint
              const isTimeOfDayValid = (timeOfDay) => {
                if (!timeOfDay || !isToday) return true
                const now = new Date()
                const [hours, minutes] = timeOfDay.split(':').map(Number)
                const timeOfDayDate = new Date()
                timeOfDayDate.setHours(hours, minutes, 0, 0)
                return timeOfDayDate <= now
              }

              // Helper function to get deployment day
              const getDeploymentDay = (deploy, firstLogin) => {
                if (!firstLogin || !deploy) return null

                if (deploy.hasOwnProperty('first_login')) {
                  const interval = deploy.first_login?.interval || 0
                  return moment(firstLogin).add(interval, 'days')
                }

                if (deploy.hasOwnProperty('days_offset') && deploy.days_offset === 0) {
                  return moment(firstLogin)
                }

                return null
              }

              // Helper function to check expiration
              const isExpired = (expire, targetMoment, firstLogin) => {
                if (!expire) return false

                // If expire.never is set, the instrument never expires
                if (expire.hasOwnProperty('never') && expire.never === null) {
                  return false
                }

                if (expire.absolute) {
                  const expirationMoment = moment.unix(expire.absolute).startOf('day')
                  return targetMoment.isAfter(expirationMoment, 'day')
                }

                if (expire.first_login && firstLogin) {
                  const expirationInterval = expire.first_login?.interval || 0
                  const expirationMoment = moment(firstLogin).add(expirationInterval, 'days').startOf('day')
                  return targetMoment.isAfter(expirationMoment, 'day')
                }

                return false
              }

              const instrumentsForDay = expandInstrumentsByInstances(
                availableInstruments
                  ?.filter((instrument) => {
                    if (!shouldKeepInstrumentInCalendarPending(instrument)) {
                      return false
                    }

                    if (
                      shouldHideNonPersistentPendingOnDay(
                        instrument,
                        targetDayMoment,
                      )
                    ) {
                      return false
                    }

                    const schedule = instrument?.metadata?.schedule
                    const scheduleId =
                      instrument?.schedule_id ||
                      instrument?.instances?.[0]?.schedule_id ||
                      instrument?.instances?.[0]?.scheduleId

                    if (isMultiScheduleMetadata(schedule)) {
                      return isInstrumentVisibleOnCalendarDay(
                        instrument,
                        targetDayMoment,
                        {
                          isToday,
                          firstLogin,
                          isTimeOfDayValid,
                          isExpired,
                        },
                      )
                    }

                  const resolvedSchedule = resolveSchedule(schedule, scheduleId)
                  const { deploy, recurring } = resolvedSchedule
                  const isPersistent = isPersistentRecurringScheme(recurring)

                  if (!isPersistent && !isInstrumentVisibleByTime(instrument)) {
                    return false
                  }

                  if (!deploy) return false

                  // GOLD-12288: visit-triggered instruments (including persistent)
                  // use days_offset: 0 in the schedule template. Hide them until
                  // the visit is confirmed or an instance has start_date.
                  if (
                    shouldHideUnconfirmedVisitInstrument(
                      schedule,
                      instrument?.instances,
                      confirmedVisitKeys,
                    )
                  ) {
                    return false
                  }

                  // Cache recurring instrument check
                  const isRecurringInstrument = recurring?.scheme === 'n-months' ||
                    recurring?.scheme === 'n-days' ||
                    recurring?.scheme === 'n-weeks'

                  // Cache deployment type checks
                  const hasDaysOffset = deploy.hasOwnProperty('days_offset')
                  const hasFirstLogin = deploy.hasOwnProperty('first_login')
                  const isDeployOnParticipantStart = (hasDaysOffset && deploy.days_offset === 0) || hasFirstLogin

                  const persistentOrVisitVisibility =
                    shouldShowPersistentOrVisitInstrumentOnDay({
                      instrument,
                      schedule: resolvedSchedule,
                      targetDayMoment,
                      isToday,
                      isTimeOfDayValid,
                      isExpired,
                      firstLogin,
                    })

                  if (persistentOrVisitVisibility !== null) {
                    return persistentOrVisitVisibility
                  }

                  if (hasAbsoluteDeploy(deploy)) {
                    return isAbsoluteDeployScheduleVisibleOnDay(
                      resolvedSchedule,
                      targetDayMoment,
                      {
                        isToday,
                        isTimeOfDayValid,
                        isExpired,
                        firstLogin,
                        areAllInstancesCompleted: areAllInstancesCompleted(
                          instrument?.instances,
                        ),
                        instances: instrument?.instances,
                      },
                    )
                  }

                  if (hasNowDeploy(deploy)) {
                    return isNowDeployScheduleVisibleOnDay(
                      resolvedSchedule,
                      targetDayMoment,
                      {
                        isToday,
                        isTimeOfDayValid,
                        isExpired,
                        firstLogin,
                        areAllInstancesCompleted: areAllInstancesCompleted(
                          instrument?.instances,
                        ),
                        instances: instrument?.instances,
                      },
                    )
                  }

                  // Non-recurring instruments with participant-based deployment
                  if (
                    isDeployOnParticipantStart &&
                    !isRecurringInstrument &&
                    !isPersistent
                  ) {
                    // Prefer instance visibility window so visible_days/end_date work,
                    // and avoid locale-sensitive YYYY-MM-DD string compares (Arabic digits).
                    const visibleViaInstance = hasActiveInstanceVisibleOnCalendarDay(
                      instrument,
                      instrument?.instances,
                      targetDayMoment,
                      resolvedSchedule,
                      firstLogin,
                    )

                    if (visibleViaInstance) {
                      if (isToday && !isTimeOfDayValid(deploy.time_of_day)) {
                        return false
                      }
                      return true
                    }

                    // Fallback when firstLogin is not available
                    if (isToday && hasDaysOffset && deploy.days_offset === 0) {
                      if (!isTimeOfDayValid(deploy.time_of_day)) {
                        return false
                      }
                      return !areAllInstancesCompleted(instrument?.instances)
                    }

                    return false
                  }

                  // Handle recurring instruments that deploy on participant start and repeat daily
                  if (isDeployOnParticipantStart && isRecurringInstrument && recurring?.scheme === 'n-days') {
                    const deploymentDay = getDeploymentDay(deploy, firstLogin)

                    if (deploymentDay) {
                      const deploymentDayMoment = deploymentDay.startOf('day')

                      // If we're on the deployment day and it's today, enforce time_of_day
                      const isDeploymentDay = targetDayMoment.isSame(deploymentDayMoment, 'day')
                      if (isDeploymentDay && isToday && !isTimeOfDayValid(deploy.time_of_day)) {
                        return false
                      }

                      // Check if the target day is on or after the deployment day
                      if (targetDayMoment.isBefore(deploymentDayMoment, 'day')) {
                        return false
                      }

                      // Check expiration
                      const { expire } = resolvedSchedule || {}
                      if (isExpired(expire, targetDayMoment, firstLogin)) {
                        return false
                      }

                      // Check if all instances are completed
                      if (areAllInstancesCompleted(instrument?.instances)) {
                        return false
                      }

                      if (
                        shouldHideNonPersistentPendingOnDay(
                          instrument,
                          targetDayMoment,
                        )
                      ) {
                        return false
                      }

                      // For daily repeating instruments, check if the day aligns with the recurring interval
                      const interval = recurring?.interval || 1
                      if (interval === 1) {
                        // Every day - show on all days from deployment day onwards
                        return true
                      }

                      // For other intervals, check if the day aligns with the pattern
                      const daysSinceDeployment = targetDayMoment.diff(deploymentDayMoment, 'days')
                      return daysSinceDeployment >= 0 && daysSinceDeployment % interval === 0
                    }

                    // Fallback when firstLogin is not available
                    if (isToday && hasDaysOffset && deploy.days_offset === 0) {
                      return !areAllInstancesCompleted(instrument?.instances)
                    }
                  }

                  if (hasAbsoluteDeploy(deploy) || hasNowDeploy(deploy)) {
                    return false
                  }

                  // Fallback: uncompleted instances visible on this calendar day
                  return hasActiveInstanceVisibleOnCalendarDay(
                    instrument,
                    instrument?.instances,
                    targetDayMoment,
                    resolvedSchedule,
                    firstLogin,
                  )
                })
                  ?.map((instrument) => {
                    const scheduleId =
                      instrument?.schedule_id ||
                      instrument?.instances?.[0]?.schedule_id ||
                      instrument?.instances?.[0]?.scheduleId
                    const resolvedSchedule = resolveSchedule(
                      instrument?.metadata?.schedule,
                      scheduleId,
                    )
                    const isPersistent = isPersistentRecurringScheme(
                      resolvedSchedule?.recurring,
                    )

                    if (!isMultiScheduleMetadata(instrument?.metadata?.schedule)) {
                      if (isPersistent) return instrument

                      return {
                        ...instrument,
                        instances: (instrument.instances || []).filter(
                          (instance) => !instance.completion,
                        ),
                      }
                    }

                    return {
                      ...instrument,
                      instances: (instrument.instances || []).filter((instance) => {
                        if (instance.completion) return false

                        const scheduleId =
                          instance.schedule_id || instance.scheduleId
                        const resolvedSchedule = resolveSchedule(
                          instrument?.metadata?.schedule,
                          scheduleId,
                        )

                        if (
                          !isInstanceVisibleOnCalendarDay(
                            instrument,
                            instance,
                            targetDayMoment,
                            resolvedSchedule,
                            moment(),
                            firstLogin,
                          )
                        ) {
                          return false
                        }

                        if (
                          isToday &&
                          !isInstanceVisibleByTime(instrument, instance)
                        ) {
                          return false
                        }

                        return true
                      }),
                    }
                  })
                  ?.filter((instrument) => instrument?.instances?.length > 0),
              )

              const instrumentsForDayDisplay = (instrumentsForDay || []).filter(
                (instrument) =>
                  shouldDisplayPendingInstrumentOnCalendarDay(
                    instrument,
                    targetDayMoment,
                    { allInstances, firstLogin },
                  ),
              )

              const completedInstrumentsForDay = mapCompletedInstrumentsForDay(
                completedInstruments,
                targetDayMoment,
              )

              const expiredInstrumentsForDay = expiredInstruments.filter(
                (instrument) => {
                  const schedules = getSchedulesList(instrument?.metadata?.schedule)
                  const expirationDates = schedules
                    .map((scheduleItem) => scheduleItem?.expire?.absolute)
                    .filter(Boolean)

                  if (!expirationDates.length) {
                    const scheduleId =
                      instrument?.schedule_id ||
                      instrument?.instances?.[0]?.schedule_id ||
                      instrument?.instances?.[0]?.scheduleId
                    const legacyExpiration = resolveSchedule(
                      instrument?.metadata?.schedule,
                      scheduleId,
                    )?.expire?.absolute
                    if (!legacyExpiration) return false
                    expirationDates.push(legacyExpiration)
                  }

                  return expirationDates.some((expirationDate) => {
                    const expirationMoment = moment.unix(expirationDate)
                    const expirationDateString = moment
                      .utc(expirationMoment)
                      .format()
                      .split('T')[0]

                    return compareDates(expirationDateString, targetDayString, 'b')
                  })
                }
              )

              const noInstruments = !instrumentsForDayDisplay?.length
              const noCompletedInstruments = !completedInstrumentsForDay?.length
              const noExpiredInstruments = !expiredInstrumentsForDay?.length

              const isDaySelected =
                !!selectedDay && moment(selectedDay).isSame(targetDayMoment, 'day')

              return (
                <Fragment key={`${targetDayString}-${idx}`}>
                  <CalendarDayOfMonth
                    day={day}
                    selectDay={selectDay}
                    selectedDay={selectedDay}
                    noInstruments={noInstruments}
                    noCompletedInstruments={noCompletedInstruments}
                    noExpiredInstruments={noExpiredInstruments}
                    instrumentsForDay={instrumentsForDayDisplay}
                    completedInstrumentsForDay={completedInstrumentsForDay}
                    expiredInstrumentsForDay={expiredInstrumentsForDay}
                    line={line}
                    selectedLine={selectedLine}
                    selectLine={selectLine}
                    month={month}
                    selectMonth={selectMonth}
                  />
                  {isDaySelected ? (
                    <CalendarExpandedDay
                      idx={idx}
                      day={selectedDay}
                      formatSubmissionSurvey={formatSubmissionSurvey}
                      strings={strings}
                      sendEvent={sendEvent}
                      selectedLocale={selectedLocale}
                      fetchInstrument={fetchInstrument}
                      setSubmissionSurvey={setSubmissionSurvey}
                      displayingList={displayingList}
                      humiIsabellaDisabled={humiIsabellaDisabled}
                      studyId={studyId}
                      noInstruments={noInstruments}
                      noCompletedInstruments={noCompletedInstruments}
                      noExpiredInstruments={noExpiredInstruments}
                      instrumentsForDay={instrumentsForDayDisplay}
                      completedInstrumentsForDay={completedInstrumentsForDay}
                      expiredInstrumentsForDay={expiredInstrumentsForDay}
                      participantId={participantId}
                      allInstances={allInstances}
                      firstLogin={firstLogin}
                    />
                  ) : null}
                </Fragment>
              )
            })}
          </div>
        </>
      ) : null}
    </div>
  )
}

export default CalendarMonthListItem
